import apiClient from './client';

export interface MedicalChatCitation {
  document_id: number;
  original_filename: string;
  document_date: string | null;
  upload_date: string | null;
  /** Mirror of the cited document's `document_types` array (the
   *  classifier-derived type list). Empty when the document has not
   *  been classified yet. */
  document_types: string[];
  relevance_note: string | null;
}

export interface MemberCandidate {
  id: number;
  name: string;
}

export type ChatHistoryTurn = { role: 'user' | 'assistant'; content: string };

export type MedicalChatAgentState = {
  member_id?: number | null;
  intent?: Record<string, unknown> | null;
  entities?: Record<string, unknown> | null;
  self_critique?: Record<string, unknown> | null;
};

export type MedicalChatAgentMeta = {
  retrieval_passes?: number;
  planner_confidence?: number | null;
  validator_triggered?: boolean;
  validator_regenerated?: boolean;
  reasoning_model?: string;
};

export interface MedicalChatResponse {
  answer: string;
  citations: MedicalChatCitation[];
  intent_summary: string | null;
  retrieved_count: number;
  candidate_pool_size: number;
  resolved_member_id: number | null;
  needs_member_clarification: boolean;
  member_candidates: MemberCandidate[] | null;
  retrieval_escalation?: boolean;
  chat_mode?: string;
  disclaimer_appended?: boolean;
  agent_meta?: MedicalChatAgentMeta | null;
  /** Extracted chain-of-thought; not part of `answer` (shown separately in UI). */
  reasoning_content?: string | null;
  agent_state?: MedicalChatAgentState | null;
  debug_trace?: Record<string, unknown> | null;
}

export type MedicalChatOptions = {
  chat_mode?: 'standard' | 'agentic';
  history?: ChatHistoryTurn[];
  /** Admin-managed focus key (e.g. `labs`, `imaging`). Validated server-side
   *  against the active focus-mode registry; any string is accepted on the
   *  wire so renaming a focus mode in the admin UI doesn't require a frontend
   *  redeploy. `null` / empty = no focus filter. */
  focus_mode?: string | null;
  agent_state?: MedicalChatAgentState | null;
  document_id?: number | null;
  debug?: boolean;
};

/** Admin-curated focus mode the chat dropdown renders. Matches the
 *  shape returned by GET /api/chat/focus-modes (public endpoint, no
 *  auth required). */
export interface FocusModeConfig {
  key: string;
  label_ro: string;
  label_en: string;
  user_suffix: string;
}

export type AgenticStreamEvent =
  | { type: 'step'; phase: string; label: string }
  | { type: 'thinking_delta'; text: string }
  | { type: 'answer_delta'; text: string }
  | { type: 'done'; response: MedicalChatResponse }
  | { type: 'error'; detail: string };

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const chatApi = {
  /** Fetch the admin-managed focus-mode list. Public endpoint, so the
   *  bubble can read it before the user signs in (though the bubble
   *  itself only mounts post-auth in practice). */
  listFocusModes: async (): Promise<FocusModeConfig[]> => {
    const response = await apiClient.get('/api/chat/focus-modes');
    return response.data;
  },

  medicalChat: async (
    memberId: number | null,
    message: string,
    language: string = 'ro',
    opts?: MedicalChatOptions
  ): Promise<MedicalChatResponse> => {
    const agentic = opts?.chat_mode === 'agentic';
    const path = agentic ? '/api/chat/medical/agentic' : '/api/chat/medical';
    const body: Record<string, unknown> = {
      member_id: memberId,
      message,
      language,
    };
    if (!agentic && opts?.chat_mode) {
      body.chat_mode = opts.chat_mode;
    }
    if (opts?.history && opts.history.length > 0) {
      body.history = opts.history;
    }
    if (opts?.focus_mode) {
      body.focus_mode = opts.focus_mode;
    }
    if (opts?.agent_state) {
      body.agent_state = opts.agent_state;
    }
    if (typeof opts?.document_id === 'number') {
      body.document_id = opts.document_id;
    }
    if (opts?.debug) {
      body.debug = true;
    }
    const response = await apiClient.post(path, body);
    return response.data;
  },

  /**
   * SSE stream (fetch + ReadableStream): steps, thinking/answer deltas, then done.
   * Pass `signal` to cancel (e.g. new message sent — only one in-flight stream per UI session).
   */
  medicalChatAgenticStream: async (
    memberId: number | null,
    message: string,
    language: string = 'ro',
    opts: MedicalChatOptions | undefined,
    onEvent: (ev: AgenticStreamEvent) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const token = localStorage.getItem('token');
    const body: Record<string, unknown> = {
      member_id: memberId,
      message,
      language,
      chat_mode: 'agentic',
    };
    if (opts?.history && opts.history.length > 0) {
      body.history = opts.history;
    }
    if (opts?.focus_mode) {
      body.focus_mode = opts.focus_mode;
    }
    if (opts?.agent_state) {
      body.agent_state = opts.agent_state;
    }
    if (typeof opts?.document_id === 'number') {
      body.document_id = opts.document_id;
    }
    if (opts?.debug) {
      body.debug = true;
    }
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api/chat/medical/agentic/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
      });
    } catch (e: unknown) {
      const aborted =
        (e instanceof DOMException && e.name === 'AbortError') ||
        (e instanceof Error && e.name === 'AbortError');
      if (aborted) return;
      throw e;
    }
    if (res.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return;
    }
    if (!res.ok) {
      let detail = `Stream failed (${res.status})`;
      try {
        const err = await res.json();
        if (err?.detail) detail = typeof err.detail === 'string' ? err.detail : detail;
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    const reader = res.body?.getReader();
    if (!reader) throw new Error('No response body');
    const decoder = new TextDecoder();
    let buffer = '';
    let sawDone = false;
    try {
      while (!signal?.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const block of parts) {
          const line = block.trim();
          if (!line.startsWith('data:')) continue;
          const jsonStr = line.slice(5).trim();
          try {
            const data = JSON.parse(jsonStr) as AgenticStreamEvent;
            onEvent(data);
            if (data.type === 'done' || data.type === 'error') {
              sawDone = true;
            }
          } catch {
            /* ignore malformed chunk */
          }
        }
      }
    } catch (e: unknown) {
      const aborted =
        (e instanceof DOMException && e.name === 'AbortError') ||
        (e instanceof Error && e.name === 'AbortError');
      if (aborted) {
        try {
          reader.releaseLock();
        } catch {
          /* ignore */
        }
        return;
      }
      throw e;
    }
    if (signal?.aborted) return;
    if (!sawDone) {
      throw new Error('Stream ended unexpectedly');
    }
  },
};
